export const DAY_LABELS = {
  mon: "Montag",
  tue: "Dienstag",
  wed: "Mittwoch",
  thu: "Donnerstag",
  fri: "Freitag",
  sat: "Samstag",
  sun: "Sonntag",
};

export const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

const KEY_BY_JS_DAY = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export function dayKey(date) {
  return KEY_BY_JS_DAY[date.getDay()];
}

function toMinutes(time) {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function localDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function rangesForDate(location, date) {
  const dateStr = localDateStr(date);
  const special = (location?.special_hours || []).find((s) => s.date === dateStr);
  if (special) {
    if (special.closed) return { ranges: [], note: special.note || "" };
    if (special.open && special.close)
      return { ranges: [{ open: special.open, close: special.close }], note: special.note || "" };
  }
  const regular = location?.opening_hours?.[dayKey(date)] || [];
  return { ranges: regular, note: special?.note || "" };
}

export function getOpenStatus(location, now = new Date()) {
  if (!location) return { state: "unknown", label: "" };
  if (location.status === "TEMPORARILY_CLOSED")
    return { state: "temp_closed", label: "Vorübergehend geschlossen" };

  const nowMin = now.getHours() * 60 + now.getMinutes();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  for (const r of rangesForDate(location, yesterday).ranges) {
    if (toMinutes(r.close) <= toMinutes(r.open) && nowMin < toMinutes(r.close)) {
      return { state: "open", label: `Jetzt geöffnet · bis ${r.close} Uhr` };
    }
  }

  const today = rangesForDate(location, now);
  for (const r of today.ranges) {
    const o = toMinutes(r.open);
    const c = toMinutes(r.close);
    if (nowMin >= o && (c <= o || nowMin < c)) {
      return { state: "open", label: `Jetzt geöffnet · bis ${r.close} Uhr` };
    }
    if (nowMin < o) {
      return { state: "closed", label: `Geschlossen · öffnet heute ${r.open} Uhr` };
    }
  }

  for (let i = 1; i <= 7; i += 1) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    const { ranges } = rangesForDate(location, d);
    if (ranges.length > 0) {
      const dayName = i === 1 ? "morgen" : DAY_LABELS[dayKey(d)];
      return { state: "closed", label: `Geschlossen · öffnet ${dayName} ${ranges[0].open} Uhr` };
    }
  }
  return { state: "closed", label: "Derzeit geschlossen" };
}

export function isPlaceholder(value) {
  return typeof value === "string" && value.includes("[");
}

export function isReachablePhone(phone) {
  return typeof phone === "string" && !phone.includes("[") && /^[+0-9][0-9 \-/()]{4,}$/.test(phone.trim());
}

export function isReachableEmail(email) {
  return typeof email === "string" && !email.includes("[") && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
}

export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function addressLine(loc) {
  const street = [loc.street, loc.house_number].filter(Boolean).join(" ").trim();
  const city = [loc.postal_code, loc.city].filter(Boolean).join(" ").trim();
  return { street, city };
}

export function mapsDirectionsUrl(loc) {
  const { street, city } = addressLine(loc);
  const destination = encodeURIComponent(`${loc.name} ${street} ${city}`.trim());
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
}

// URL-Hygiene: nur http(s)- oder Root-Pfade als Bildquellen zulassen
export function safeImageUrl(url) {
  if (typeof url !== "string") return null;
  const v = url.trim();
  if (v.startsWith("/") && !v.startsWith("//")) return v;
  if (/^https:\/\/\S+$/.test(v)) return v;
  return null;
}
